import React from "react";

import { Badge, Heading, KeyValue, Text } from "@/components/ui";
import type { ProgrammeGovernanceSummary } from "@/platform";

export interface GovernedRevisionsProps {
  governance: ProgrammeGovernanceSummary;
  submittedRevision: ProgrammeGovernanceSummary["governanceRevisions"][number] | undefined;
  approvedRevision: ProgrammeGovernanceSummary["governanceRevisions"][number] | undefined;
}

type Revision = ProgrammeGovernanceSummary["governanceRevisions"][number];

function RevisionValue({
  revision,
  tone,
}: {
  revision: Revision;
  tone: "warning" | "success";
}): React.ReactElement {
  return (
    <>
      <Badge tone={tone}>Revision {revision.revisionNumber}</Badge>
      <Text as="span" size="sm" tone="muted" className="block font-mono font-normal">
        Hash {revision.contentHash.slice(0, 16)}…
      </Text>
    </>
  );
}

export function GovernedRevisions({
  governance,
  submittedRevision,
  approvedRevision,
}: GovernedRevisionsProps): React.ReactElement | null {
  if (!submittedRevision && !approvedRevision) {
    return null;
  }

  return (
    <section className="mt-4" data-testid="governance-revision-context">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Heading level={3} size="md">
            Governed revisions
          </Heading>
          <Text tone="muted" className="mt-1">
            Review and approval records belong to these locked content revisions.
          </Text>
        </div>
        <Badge variant="outline">{governance.governanceRevisions.length} recorded</Badge>
      </div>
      <KeyValue
        className="mt-3"
        entries={[
          ...(submittedRevision
            ? [
                {
                  label: "Submitted for review",
                  value: <RevisionValue revision={submittedRevision} tone="warning" />,
                  "data-testid": "submitted-revision",
                },
              ]
            : []),
          ...(approvedRevision
            ? [
                {
                  label: "Latest approved",
                  value: <RevisionValue revision={approvedRevision} tone="success" />,
                  "data-testid": "approved-revision",
                },
              ]
            : []),
        ]}
      />
    </section>
  );
}
